// Forgot Password page for password reset functionality
'use client';

import { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getAuth } from '@/lib/firebase/core';
import { AuthPageLayout } from '@/components/auth/shared/AuthPageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import Link from 'next/link';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const auth = getAuth();
      if (!auth) {
        throw new Error('Firebase Auth not available');
      }

      // Use the current domain for the continue URL
      // This domain needs to be authorized in Firebase Console
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      };

      // Add tenant-specific logging for debugging
      console.log('Sending password reset email with config:', {
        tenantId: auth.tenantId,
        projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
        continueUrl: actionCodeSettings.url
      });

      await sendPasswordResetEmail(auth, email, actionCodeSettings);

      setEmailSent(true);
      setMessage({ 
        type: 'success', 
        text: 'Password reset email sent! Please check your inbox and follow the instructions.' 
      });
    } catch (error: any) {
      const currentAuth = getAuth();
      console.error('Password reset error details:', {
        code: error.code,
        message: error.message,
        tenantId: currentAuth?.tenantId,
        continueUrl: `${window.location.origin}/login`,
        stack: error.stack
      });
      
      let errorMessage = 'Failed to send password reset email. Please try again.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please wait a moment before trying again.';
      } else if (error.code === 'auth/invalid-tenant-id') {
        errorMessage = 'Authentication service configuration error. Please contact support.';
      } else if (error.code === 'auth/tenant-not-found') {
        errorMessage = 'Authentication service not properly configured. Please contact support.';
      } else if (error.code === 'auth/unauthorized-continue-uri' || error.code === 'auth/invalid-continue-uri') {
        // Enhanced error message for domain authorization issues
        const currentDomain = window.location.origin;
        errorMessage = `Domain authorization required. The domain "${currentDomain}" needs to be added to Firebase Console authorized domains. Please contact your administrator to add this domain to the Firebase project settings.`;
        
        // Log detailed information for administrators
        console.error('FIREBASE DOMAIN AUTHORIZATION REQUIRED:', {
          domain: currentDomain,
          environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
          projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
          tenantId: currentAuth?.tenantId,
          instructions: 'Add this domain to Firebase Console > Authentication > Settings > Authorized domains'
        });
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = () => {
    setEmailSent(false);
    setMessage(null);
    handleSubmit(new Event('submit') as any);
  };

  if (emailSent) {
    return (
      <AuthPageLayout
        title="Check Your Email"
        description="We've sent password reset instructions to your email address"
      >
        <div className="space-y-6">
          {message && (
            <Alert className={message.type === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              {message.type === 'success' ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                We sent a password reset link to:
              </p>
              <p className="font-medium text-foreground">{email}</p>
            </div>

            <div className="space-y-3 pt-4">
              <p className="text-sm text-muted-foreground">
                Didn't receive the email? Check your spam folder or
              </p>
              
              <Button
                type="button"
                variant="outline"
                onClick={handleResendEmail}
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? 'Sending...' : 'Resend Email'}
              </Button>
            </div>
          </div>

          <div className="pt-6 border-t border-border">
            <Link
              href="/login"
              className="inline-flex items-center text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Reset Your Password"
      description="Enter your email address and we'll send you a link to reset your password"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {message && (
          <Alert className={message.type === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
            className="w-full"
          />
        </div>

        <Button
          type="submit"
          disabled={isLoading || !email.trim()}
          className="w-full"
        >
          {isLoading ? 'Sending Reset Email...' : 'Send Reset Email'}
        </Button>

        <div className="pt-6 border-t border-border text-center">
          <Link
            href="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>
        </div>
      </form>
    </AuthPageLayout>
  );
} 